import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ClockIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Activity = () => {
  const { makeAuthenticatedRequest, isLoading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });

  useEffect(() => {
    if (authLoading) return;
    fetchLogs();
  }, [token, authLoading]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await makeAuthenticatedRequest('/audit-logs?limit=50');
      if (data.success) {
        const normalized = Array.isArray(data.data) ? data.data : [];
        setLogs(normalized);
        if (data.pagination) {
          setPagination(data.pagination);
        }
      } else {
        setLogs([]);
        toast.error(data.message || 'Failed to load activity');
      }
    } catch (e) {
      console.error('Error fetching logs:', e);
      setLogs([]);
      toast.error(e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getActionIcon = (action) => {
    switch (action.toLowerCase()) {
      case 'customer_created':
      case 'create':
        return <PlusIcon className="h-5 w-5" />;
      case 'customer_edit':
      case 'update':
        return <PencilSquareIcon className="h-5 w-5" />;
      case 'customer_authorized':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'customer_rejected':
        return <XCircleIcon className="h-5 w-5" />;
      case 'customer_cancelled':
      case 'delete':
        return <XCircleIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case 'customer_created':
      case 'create':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'customer_edit':
      case 'update':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'customer_authorized':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'customer_rejected':
      case 'customer_cancelled':
      case 'delete':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getActionLabel = (action, resourceType) => {
    const actionLower = action.toLowerCase();
    const resourceTypeLower = resourceType?.toLowerCase();
    
    // Custom labels for specific action + resource combinations
    if (resourceTypeLower === 'claimant') {
      const claimantLabels = {
        'create': 'Created Claimant',
        'update': 'Updated Claimant',
        'delete': 'Deleted Claimant'
      };
      if (claimantLabels[actionLower]) return claimantLabels[actionLower];
    }
    
    if (resourceTypeLower === 'job') {
      const jobLabels = {
        'customer_created': 'Created Appointment',
        'customer_edit': 'Edited Appointment',
        'customer_authorized': 'Authorized Appointment',
        'customer_rejected': 'Declined Appointment',
        'customer_cancelled': 'Cancelled Appointment'
      };
      if (jobLabels[actionLower]) return jobLabels[actionLower];
    }
    
    // Generic labels
    const labels = {
      'customer_created': 'Created Appointment',
      'customer_edit': 'Edited Appointment',
      'customer_authorized': 'Authorized Appointment',
      'customer_rejected': 'Declined Appointment',
      'customer_cancelled': 'Cancelled Appointment',
      'create': 'Created',
      'update': 'Updated',
      'delete': 'Deleted'
    };
    return labels[actionLower] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFieldName = (field) => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim();
  };

  const renderChanges = (log) => {
    try {
      let changes = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
      
      // Handle both flat array format and nested changes format
      if (changes && changes.changes && Array.isArray(changes.changes)) {
        changes = changes.changes;
      }
      
      if (!Array.isArray(changes)) {
        return <span className="text-sm text-gray-500">No details available</span>;
      }

      const isExpanded = expandedLogs.has(log.id);
      const visibleChanges = isExpanded ? changes : changes.slice(0, 2);
      const hasMore = changes.length > 2;

      return (
        <div className="space-y-2">
          {visibleChanges.map((change, idx) => (
            <div key={idx} className="flex items-start space-x-2 text-sm">
              <span className="font-medium text-gray-700 min-w-[120px]">
                {formatFieldName(change.field)}:
              </span>
              <div className="flex items-center space-x-2 flex-1">
                {change.oldValue !== undefined && change.oldValue !== null && (
                  <>
                    <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded line-through">
                      {String(change.oldValue).substring(0, 50)}
                      {String(change.oldValue).length > 50 && '...'}
                    </span>
                    <span className="text-gray-400">→</span>
                  </>
                )}
                <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded font-medium">
                  {String(change.newValue).substring(0, 50)}
                  {String(change.newValue).length > 50 && '...'}
                </span>
              </div>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(log.id);
              }}
              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 font-medium mt-2"
            >
              {isExpanded ? (
                <>
                  <ChevronUpIcon className="h-4 w-4" />
                  <span>Show less</span>
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-4 w-4" />
                  <span>Show {changes.length - 2} more change{changes.length - 2 !== 1 ? 's' : ''}</span>
                </>
              )}
            </button>
          )}
        </div>
      );
    } catch (e) {
      return <span className="text-sm text-gray-500">{log.details ? JSON.stringify(log.details) : 'No details'}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ClockIcon className="h-8 w-8 mr-3 text-blue-600" />
              Activity History
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Track all your appointments and actions
            </p>
          </div>
          <button
            onClick={fetchLogs}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className="h-5 w-5 text-gray-600" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {logs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Activities</p>
                <p className="text-2xl font-bold text-gray-900">{pagination.total || logs.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last Activity</p>
                <p className="text-lg font-semibold text-gray-900">
                  {logs.length > 0 ? formatDate(logs[0].created_at) : 'N/A'}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PencilSquareIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Actions This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {logs.filter(log => {
                    const logDate = new Date(log.created_at);
                    const now = new Date();
                    return logDate.getMonth() === now.getMonth() && logDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Activity Timeline */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-blue-100">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        </div>

        <div className="divide-y divide-gray-200">
          <AnimatePresence>
            {logs.length > 0 ? (
              logs.map((log, index) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (log.resource_type === 'job' && log.resource_id) {
                      navigate(`/appointments/${log.resource_id}`);
                    } else if (log.resource_type === 'claimant' && log.resource_id) {
                      navigate(`/claimants`);
                    }
                  }}
                >
                  <div className="flex items-start space-x-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 p-3 rounded-lg border ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-gray-900">
                            {getActionLabel(log.action, log.resource_type)}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {log.resource_display || `${log.resource_type} #${log.resource_id}`}
                          </p>
                          {log.changed_by_name && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center">
                              <span className="inline-flex items-center">
                                <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {log.changed_by_name}
                                {log.changed_by_type && log.changed_by_type !== 'customer' && (
                                  <span className="ml-1 text-blue-600">({log.changed_by_type})</span>
                                )}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 ml-4 text-right">
                          <p className="text-sm text-gray-500">{formatDate(log.created_at)}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(log.created_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Changes */}
                      {log.details && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                          {renderChanges(log)}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="p-12 text-center">
                <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No activity yet</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Your appointment actions will appear here
                </p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Activity;
